export type StoryPlayMode = "freeform_novel" | "narrative_rpg";
export type StoryPresentationVariant = "standard" | "dialogue";
export type ThemeBaseMode = "dark" | "light";

export interface ProjectThemeColors {
  background: string;
  foreground: string;
  surface: string;
  surfaceForeground: string;
  primary: string;
  primaryForeground: string;
  accent: string;
  accentForeground: string;
  muted: string;
  mutedForeground: string;
  border: string;
  input: string;
  ring: string;
  destructive: string;
  destructiveForeground: string;
  success: string;
  successForeground: string;
}

export interface ProjectTheme {
  presetId: string;
  baseMode: ThemeBaseMode;
  fonts: {
    heading: string;
    body: string;
  };
  radius: {
    control: string;
    card: string;
    panel: string;
    modal: string;
  };
  colors: ProjectThemeColors;
  elevation: "flat" | "soft" | "cinematic";
  density: "compact" | "comfortable";
}

export const DEFAULT_PROJECT_THEME: ProjectTheme = {
  presetId: "private-fiction-rounded",
  baseMode: "dark",
  fonts: { heading: "Georgia, 'Noto Serif Thai', serif", body: "Inter, 'Noto Sans Thai', sans-serif" },
  radius: { control: "0.75rem", card: "1.125rem", panel: "1.375rem", modal: "1.75rem" },
  colors: {
    background: "#121012",
    foreground: "#f3eef1",
    surface: "#1b181b",
    surfaceForeground: "#f3eef1",
    primary: "#ff6b35",
    primaryForeground: "#ffffff",
    accent: "#ffc46b",
    accentForeground: "#21130d",
    muted: "#252125",
    mutedForeground: "#a8a4a2",
    border: "rgba(255,245,238,.12)",
    input: "#211e21",
    ring: "#ff8b61",
    destructive: "#df343d",
    destructiveForeground: "#ffffff",
    success: "#289c61",
    successForeground: "#ffffff",
  },
  elevation: "soft",
  density: "comfortable",
};

type ThemePresetOverrides = Partial<Omit<ProjectTheme, "fonts" | "radius" | "colors">> & {
  fonts?: Partial<ProjectTheme["fonts"]>;
  radius?: Partial<ProjectTheme["radius"]>;
  colors?: Partial<ProjectThemeColors>;
};

function themePreset(id: string, overrides: ThemePresetOverrides): ProjectTheme {
  return {
    ...DEFAULT_PROJECT_THEME,
    ...overrides,
    presetId: id,
    fonts: { ...DEFAULT_PROJECT_THEME.fonts, ...overrides.fonts },
    radius: { ...DEFAULT_PROJECT_THEME.radius, ...overrides.radius },
    colors: { ...DEFAULT_PROJECT_THEME.colors, ...overrides.colors },
  };
}

/** Visual presets are token bundles, never fixed component trees. */
export const PROJECT_THEME_PRESETS: ProjectTheme[] = [
  DEFAULT_PROJECT_THEME,



  themePreset("soft-slice", {
    baseMode: "light",
    fonts: { heading: "Mali, 'Noto Sans Thai', sans-serif", body: "Inter, 'Noto Sans Thai', sans-serif" },
    radius: { control: "1rem", card: "1.5rem", panel: "1.75rem", modal: "2rem" },
    colors: { background: "#fff9f6", foreground: "#3e3135", surface: "#f1dce4", surfaceForeground: "#38222d", primary: "#b52e60", primaryForeground: "#ffffff", accent: "#8c4564", accentForeground: "#ffffff", muted: "#e5c5d2", mutedForeground: "#65404f", input: "#fff8fa", ring: "#ef7894", border: "rgba(97,65,76,.13)", destructive: "#cc4754", success: "#48a979" },
    elevation: "soft",
  }),
  themePreset("archive-history", {
    baseMode: "light",
    fonts: { heading: "Cormorant Garamond, 'Noto Serif Thai', serif", body: "Lora, 'Noto Serif Thai', serif" },
    radius: { control: ".6rem", card: ".9rem", panel: "1.1rem", modal: "1.35rem" },
    colors: { background: "#eee4ce", foreground: "#30271f", surface: "#dfcbaa", surfaceForeground: "#30271f", primary: "#7a4d2c", primaryForeground: "#fff8ea", accent: "#b8874d", accentForeground: "#25170d", muted: "#e5d7bc", mutedForeground: "#756454", input: "#f3e8d2", ring: "#8c5b35", border: "rgba(72,49,31,.2)", destructive: "#923c32", success: "#557a52" },
    elevation: "flat",
  }),
  themePreset("crimson-horror", {
    fonts: { heading: "UnifrakturCook, 'Noto Serif Thai', serif", body: "Inter, 'Noto Sans Thai', sans-serif" },
    colors: { background: "#090708", surface: "#291820", primary: "#a91f35", accent: "#d7b894", accentForeground: "#24170f", muted: "#20171a", input: "#1a1215", ring: "#d43149", border: "rgba(169,31,53,.28)", destructive: "#e33145" },
    elevation: "cinematic",
  }),


  themePreset("noir", {
    fonts: { heading: "Cormorant Garamond, 'Noto Serif Thai', serif", body: "Inter, 'Noto Sans Thai', sans-serif" },
    radius: { control: ".55rem", card: ".8rem", panel: "1rem", modal: "1.2rem" },
    colors: { background: "#080808", surface: "#252525", primary: "#e8e8e8", primaryForeground: "#111111", accent: "#d9a56d", accentForeground: "#21170f", muted: "#252525", input: "#1b1b1b", ring: "#d9a56d", border: "rgba(255,255,255,.16)" },
    elevation: "flat", density: "compact",
  }),



];

export interface ThemeFlowTokens {
  flow0: string;
  flow1: string;
  flow2: string;
  flow3: string;
  flow4: string;
  flowSpeed: number;
  flowWarp: number;
  flowGrain: number;
  veil: string;
  edge: string;
}

export const EMBER_FLOW_TOKENS: ThemeFlowTokens = {
  flow0: "#100e11",
  flow1: "#371a17",
  flow2: "#f46c28",
  flow3: "#842d1c",
  flow4: "#ffcfad",
  flowSpeed: 1.5,
  flowWarp: 0.85,
  flowGrain: 0.045,
  veil: "rgba(12, 10, 13, .61)",
  edge: "rgba(12, 10, 13, .24)",
};

export const BLUSH_FLOW_TOKENS: ThemeFlowTokens = {
  flow0: "#fff8f8",
  flow1: "#f3dce7",
  flow2: "#f0b4cf",
  flow3: "#daceea",
  flow4: "#fff3e8",
  flowSpeed: 1.5,
  flowWarp: 0.85,
  flowGrain: 0.035,
  veil: "rgba(255, 248, 251, .45)",
  edge: "rgba(249, 232, 239, .15)",
};

export const OXBLOOD_FLOW_TOKENS: ThemeFlowTokens = {
  flow0: "#100e11",
  flow1: "#3a0c19",
  flow2: "#881c31",
  flow3: "#400d1d",
  flow4: "#f3dfbe",
  flowSpeed: 1.5,
  flowWarp: 0.85,
  flowGrain: 0.05,
  veil: "rgba(12, 8, 13, .55)",
  edge: "rgba(12, 8, 13, .23)",
};

export const PARCHMENT_FLOW_TOKENS: ThemeFlowTokens = {
  flow0: "#fffdf7",
  flow1: "#eee0c8",
  flow2: "#c7ac85",
  flow3: "#e1cdaa",
  flow4: "#fff9ee",
  flowSpeed: 1.5,
  flowWarp: 0.85,
  flowGrain: 0.11,
  veil: "rgba(255, 251, 239, .42)",
  edge: "rgba(224, 207, 177, .15)",
};

export function canonicalThemeName(presetId: string): string {
  if (presetId === "private-fiction-rounded" || presetId === "ember") return "ember";
  if (presetId === "soft-slice" || presetId === "blush") return "blush";
  if (presetId === "crimson-horror" || presetId === "oxblood") return "oxblood";
  if (presetId === "archive-history" || presetId === "parchment") return "parchment";
  return presetId;
}

export function computeThemeFlowTokens(theme: ProjectTheme): ThemeFlowTokens {
  const canonical = canonicalThemeName(theme.presetId);
  if (canonical === "ember") return EMBER_FLOW_TOKENS;
  if (canonical === "blush") return BLUSH_FLOW_TOKENS;
  if (canonical === "oxblood") return OXBLOOD_FLOW_TOKENS;
  if (canonical === "parchment") return PARCHMENT_FLOW_TOKENS;

  const isLight = theme.baseMode === "light";
  return {
    flow0: theme.colors.background || (isLight ? "#fff8f8" : "#100e11"),
    flow1: theme.colors.surface || (isLight ? "#f3dce7" : "#371a17"),
    flow2: theme.colors.primary || (isLight ? "#f0b4cf" : "#f46c28"),
    flow3: theme.colors.muted || (isLight ? "#daceea" : "#842d1c"),
    flow4: theme.colors.accent || (isLight ? "#fff3e8" : "#ffcfad"),
    flowSpeed: 1.5,
    flowWarp: 0.85,
    flowGrain: isLight ? 0.04 : 0.045,
    veil: isLight ? "rgba(255, 248, 251, .45)" : "rgba(12, 10, 13, .61)",
    edge: isLight ? "rgba(249, 232, 239, .15)" : "rgba(12, 10, 13, .24)",
  };
}

export function getProjectThemePresets(): ProjectTheme[] {
  return PROJECT_THEME_PRESETS.map((preset) => structuredClone(preset));
}

export function getProjectThemePreset(id: string): ProjectTheme {
  const canonical = canonicalThemeName(id);
  return structuredClone(
    PROJECT_THEME_PRESETS.find((preset) => preset.presetId === id || canonicalThemeName(preset.presetId) === canonical) ?? DEFAULT_PROJECT_THEME
  );
}

const object = (value: unknown): Record<string, unknown> => value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
const string = (value: unknown, fallback: string): string => typeof value === "string" && value.trim() ? value : fallback;

export function normalizeStoryPlayMode(value: unknown, legacyMode: unknown = "", hasRpgMechanics = false): StoryPlayMode {
  if (value === "freeform_novel" || value === "narrative_rpg") return value;
  if (legacyMode === "chat") return "freeform_novel";
  return hasRpgMechanics ? "narrative_rpg" : "freeform_novel";
}

export function normalizeProjectTheme(value: unknown): ProjectTheme {
  const source = object(value);
  const fonts = object(source.fonts);
  const radius = object(source.radius);
  const colors = object(source.colors);
  const defaults = DEFAULT_PROJECT_THEME;
  return {
    presetId: string(source.presetId, defaults.presetId),
    baseMode: source.baseMode === "light" ? "light" : "dark",
    fonts: {
      heading: string(fonts.heading, defaults.fonts.heading),
      body: string(fonts.body, defaults.fonts.body),
    },
    radius: {
      control: string(radius.control, defaults.radius.control),
      card: string(radius.card, defaults.radius.card),
      panel: string(radius.panel, defaults.radius.panel),
      modal: string(radius.modal, defaults.radius.modal),
    },
    colors: {
      background: string(colors.background, defaults.colors.background),
      foreground: string(colors.foreground, defaults.colors.foreground),
      surface: string(colors.surface, defaults.colors.surface),
      surfaceForeground: string(colors.surfaceForeground, defaults.colors.surfaceForeground),
      primary: string(colors.primary, defaults.colors.primary),
      primaryForeground: string(colors.primaryForeground, defaults.colors.primaryForeground),
      accent: string(colors.accent, defaults.colors.accent),
      accentForeground: string(colors.accentForeground, defaults.colors.accentForeground),
      muted: string(colors.muted, defaults.colors.muted),
      mutedForeground: string(colors.mutedForeground, defaults.colors.mutedForeground),
      border: string(colors.border, defaults.colors.border),
      input: string(colors.input, defaults.colors.input),
      ring: string(colors.ring, defaults.colors.ring),
      destructive: string(colors.destructive, defaults.colors.destructive),
      destructiveForeground: string(colors.destructiveForeground, defaults.colors.destructiveForeground),
      success: string(colors.success, defaults.colors.success),
      successForeground: string(colors.successForeground, defaults.colors.successForeground),
    },
    elevation: source.elevation === "flat" || source.elevation === "cinematic" ? source.elevation : "soft",
    density: source.density === "compact" ? "compact" : "comfortable",
  };
}

export function projectThemeCssVariables(theme: ProjectTheme): Record<string, string> {
  const accessible = { ...theme, colors: accessibleThemeColors(theme.colors) };
  const flow = computeThemeFlowTokens(theme);
  return {
    "--text-primary": accessible.colors.foreground,
    "--text-secondary": accessible.colors.mutedForeground,
    "--text-muted": accessible.colors.mutedForeground,
    "--muted": accessible.colors.mutedForeground,
    "--ink": accessible.colors.background,
    "--surface": accessible.colors.surface,
    "--surface-2": accessible.colors.muted,
    "--line": accessible.colors.border,
    "--mint": accessible.colors.primary,
    "--gold": accessible.colors.accent,
    "--pf-primary-text": readableForeground(accessible.colors.background, accessible.colors.primary),
    "--pf-input-foreground": readableForeground(accessible.colors.input, accessible.colors.foreground),
    "--pf-background": accessible.colors.background,
    "--pf-foreground": accessible.colors.foreground,
    "--pf-surface": accessible.colors.surface,
    "--pf-surface-foreground": accessible.colors.surfaceForeground,
    "--pf-primary": accessible.colors.primary,
    "--pf-primary-foreground": accessible.colors.primaryForeground,
    "--pf-accent": accessible.colors.accent,
    "--pf-accent-foreground": accessible.colors.accentForeground,
    "--pf-muted": accessible.colors.muted,
    "--pf-muted-foreground": accessible.colors.mutedForeground,
    "--pf-border": accessible.colors.border,
    "--pf-input": accessible.colors.input,
    "--pf-ring": accessible.colors.ring,
    "--pf-destructive": accessible.colors.destructive,
    "--pf-destructive-foreground": accessible.colors.destructiveForeground,
    "--pf-success": accessible.colors.success,
    "--pf-success-foreground": accessible.colors.successForeground,
    "--pf-font-heading": accessible.fonts.heading,
    "--pf-font-body": accessible.fonts.body,
    "--pf-radius-control": accessible.radius.control,
    "--pf-radius-card": accessible.radius.card,
    "--pf-radius-panel": accessible.radius.panel,
    "--pf-radius-modal": accessible.radius.modal,
    "--flow-0": flow.flow0,
    "--flow-1": flow.flow1,
    "--flow-2": flow.flow2,
    "--flow-3": flow.flow3,
    "--flow-4": flow.flow4,
    "--flow-speed": String(flow.flowSpeed),
    "--flow-warp": String(flow.flowWarp),
    "--flow-grain": String(flow.flowGrain),
    "--veil": flow.veil,
    "--edge": flow.edge,
  };
}

function luminance(color: string): number | null {
  const match = color.match(/^#([\da-f]{3}|[\da-f]{6})$/i);
  if (!match) return null;
  const hex = match[1]!.length === 3 ? [...match[1]!].map(c => c + c).join("") : match[1]!;
  return [0, 2, 4].reduce((sum, start, i) => { const c = parseInt(hex.slice(start, start + 2), 16) / 255; return sum + (c <= .04045 ? c / 12.92 : ((c + .055) / 1.055) ** 2.4) * [ .2126, .7152, .0722 ][i]!; }, 0);
}
export function contrastRatio(a: string, b: string): number {
  const x = luminance(a), y = luminance(b);
  return x === null || y === null ? 0 : (Math.max(x, y) + .05) / (Math.min(x, y) + .05);
}
/** Preserve the chosen hue; only repair the foreground paired with that surface. */
export function readableForeground(background: string, preferred: string): string {
  if (luminance(background) === null || contrastRatio(background, preferred) >= 4.5) return preferred;
  const neutral = contrastRatio(background, "#f7f7f8") >= contrastRatio(background, "#141416") ? "#f7f7f8" : "#141416";
  if (contrastRatio(background, neutral) >= 4.5) return neutral;
  return contrastRatio(background, '#ffffff') >= contrastRatio(background, '#000000') ? '#ffffff' : '#000000';
}
export function accessibleThemeColors(colors: ProjectThemeColors): ProjectThemeColors {
  return { ...colors,
    foreground: readableForeground(colors.background, colors.foreground),
    surfaceForeground: readableForeground(colors.surface, colors.surfaceForeground),
    mutedForeground: readableForeground(colors.muted, readableForeground(colors.background, colors.mutedForeground)),
    primaryForeground: readableForeground(colors.primary, colors.primaryForeground),
    accentForeground: readableForeground(colors.accent, colors.accentForeground),
    successForeground: readableForeground(colors.success, colors.successForeground),
    destructiveForeground: readableForeground(colors.destructive, colors.destructiveForeground),
  };
}
